// src/Pages/Commerce/salesImport.utils.js
//
// Pure CSV parsing for the "Import sales history" feature — lets a merchant
// bring in past orders from their existing online store (WooCommerce order
// exports are the common case, but any CSV with recognizable columns works)
// so that revenue isn't a blank slate the day they switch to Blitz Commerce.
//
// Deliberately separate from src/Pages/BlitzPay/t2a/csv.utils.js — that
// parser expects a transfer-recipient shape (Name/Email/Amount); this one
// expects an order shape (order id, date, customer, product, total, status)
// and is fuzzy-matched against common store-export header names rather than
// one fixed template.

function splitLine(line) {
  const result = [];
  let cur = "", inQ = false;
  for (const ch of line) {
    if (ch === '"') { inQ = !inQ; continue; }
    if (ch === "," && !inQ) { result.push(cur.trim()); cur = ""; continue; }
    cur += ch;
  }
  result.push(cur.trim());
  return result;
}

// Priority-ordered, specific keyword sets — kept specific (not bare "name"
// or "id") so e.g. a "Product Name" column doesn't get mistaken for the
// customer's name, and "Order ID" doesn't get mistaken for a generic id.
const COLUMN_KEYWORDS = {
  orderId:  ["order id", "order number", "order #", "order no"],
  date:     ["order date", "date paid", "date"],
  name:     ["customer name", "billing name", "buyer name", "full name"],
  firstName: ["first name", "billing first name"],
  lastName:  ["last name", "billing last name"],
  email:    ["email"],
  product:  ["item names", "product names", "products purchased", "product", "item"],
  amount:   ["order total", "total", "grand total", "amount", "price"],
  status:   ["order status", "status"],
};

function findColumn(headers, keywords) {
  for (const kw of keywords) {
    const idx = headers.findIndex((h) => h.includes(kw));
    if (idx !== -1) return idx;
  }
  return -1;
}

const STATUS_MAP = {
  completed: "fulfilled", complete: "fulfilled", fulfilled: "fulfilled", paid: "fulfilled",
  processing: "pending", pending: "pending", "on hold": "pending", "on-hold": "pending",
  refunded: "refunded", cancelled: "refunded", canceled: "refunded", failed: "refunded",
};
const normalizeStatus = (raw) => STATUS_MAP[(raw || "").toLowerCase().trim()] || "fulfilled";

/**
 * Parse CSV text into historical-order rows.
 * Required (per row, after parsing): customer_name OR customer_email, and amount.
 * Everything else is best-effort / optional.
 *
 * @returns {{
 *   rows: Array<{
 *     external_order_id: string, order_date: string, customer_name: string,
 *     customer_email: string, product_name: string, amount: string,
 *     status: string, needsAmount: boolean, needsContact: boolean,
 *   }>,
 *   errors: string[],
 * }}
 */
export function parseSalesCSV(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) {
    return { rows: [], errors: ["CSV needs a header row and at least one data row."] };
  }

  const rawHeaders = splitLine(lines[0]);
  const headers = rawHeaders.map((h) => h.toLowerCase().replace(/['"]/g, "").trim());

  const idx = {
    orderId: findColumn(headers, COLUMN_KEYWORDS.orderId),
    date: findColumn(headers, COLUMN_KEYWORDS.date),
    name: findColumn(headers, COLUMN_KEYWORDS.name),
    firstName: findColumn(headers, COLUMN_KEYWORDS.firstName),
    lastName: findColumn(headers, COLUMN_KEYWORDS.lastName),
    email: findColumn(headers, COLUMN_KEYWORDS.email),
    product: findColumn(headers, COLUMN_KEYWORDS.product),
    amount: findColumn(headers, COLUMN_KEYWORDS.amount),
    status: findColumn(headers, COLUMN_KEYWORDS.status),
  };

  const errs = [];
  if (idx.name === -1 && idx.firstName === -1 && idx.email === -1) {
    errs.push('Missing a "Customer Name" or "Email" column.');
  }
  if (idx.amount === -1) errs.push('Missing an "Order Total" / "Amount" column.');
  if (errs.length) return { rows: [], errors: errs };

  const rows = [];
  const rowErrors = [];
  const clean = (cols, i) => (i !== -1 ? (cols[i] || "").replace(/^"|"$/g, "").trim() : "");

  lines.slice(1).forEach((line, i) => {
    if (!line.trim()) return;
    const cols = splitLine(line);

    const name = idx.name !== -1
      ? clean(cols, idx.name)
      : [clean(cols, idx.firstName), clean(cols, idx.lastName)].filter(Boolean).join(" ");
    const email = clean(cols, idx.email);
    const rawAmt = clean(cols, idx.amount);
    const amount = rawAmt ? parseFloat(rawAmt.replace(/[$,\s]/g, "")) : NaN;

    if (!name && !email) {
      rowErrors.push(`Row ${i + 2}: no customer name or email — skipped.`);
      return;
    }

    rows.push({
      external_order_id: clean(cols, idx.orderId) || null,
      order_date: clean(cols, idx.date) || null,
      customer_name: name || email,
      customer_email: email,
      product_name: clean(cols, idx.product) || "Imported order",
      amount: !isNaN(amount) && amount > 0 ? amount.toString() : "",
      status: normalizeStatus(clean(cols, idx.status)),
      needsAmount: isNaN(amount) || amount <= 0,
      needsContact: !email,
    });
  });

  return { rows, errors: rowErrors };
}

/** Generate a downloadable CSV template blob URL matching a typical WooCommerce order export. */
export function salesTemplateUrl() {
  const csv =
    "Order ID,Order Date,Customer Name,Email,Product,Order Total,Order Status\n" +
    "1042,2026-05-14,Jane Buyer,jane@example.com,Cookie Dozen,24.00,Completed\n" +
    "1043,2026-05-15,John Smith,john@example.com,Cookie Dozen x2,48.00,Completed\n";
  const blob = new Blob([csv], { type: "text/csv" });
  return URL.createObjectURL(blob);
}
